import { useState } from 'react';
import { Box, Button, IconButton, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import type { IDeliverable, DeliverableType, DeliverablePlatform } from '../../redux/buzzbridge/types';
import { DeliverablePickerModal } from './DeliverablePickerModal';

const TYPE_LABELS: Record<DeliverableType, string> = {
    instagram_post: 'Instagram Post',
    instagram_reel: 'Instagram Reel',
    instagram_story: 'Instagram Story',
    tiktok_video: 'TikTok Video',
    tiktok_short: 'TikTok Short',
    youtube_short: 'YouTube Short',
    youtube_video: 'YouTube Video',
    twitter_post: 'Twitter/X Post',
    facebook_post: 'Facebook Post',
    ugc_video: 'UGC Video',
    ugc_photo: 'UGC Photo',
    vertical: 'Vertical Video',
    horizontal: 'Horizontal Video',
};

const PLATFORM_COLORS: Record<DeliverablePlatform, string> = {
    instagram: '#c13584',
    tiktok: '#69c9d0',
    youtube: '#ff0000',
    twitter: '#1da1f2',
    x: '#9e9e9e',
    facebook: '#1877f2',
};

const PLATFORM_BG: Record<DeliverablePlatform, string> = {
    instagram: '#c1358418',
    tiktok: '#69c9d018',
    youtube: '#ff000018',
    twitter: '#1da1f218',
    x: '#9e9e9e18',
    facebook: '#1877f218',
};

interface Props {
    items: IDeliverable[];
    onItemsChange: (items: IDeliverable[]) => void;
    selectedPlatforms?: string[];
    isDirectHire?: boolean;
    campaignType?: 'standard' | 'direct_hire' | 'sync_upload' | 'commission';
}

export function DeliverableList({
    items,
    onItemsChange,
    selectedPlatforms,
    isDirectHire = false,
    campaignType,
}: Props) {
    const [addOpen, setAddOpen] = useState(false);
    const [editIndex, setEditIndex] = useState<number | null>(null);

    const handleAdd = (d: IDeliverable) => {
        onItemsChange([...items, d]);
    };

    const handleEdit = (d: IDeliverable) => {
        if (editIndex === null) return;
        const updated = [...items];
        updated[editIndex] = d;
        onItemsChange(updated);
        setEditIndex(null);
    };

    const handleRemove = (index: number) => {
        onItemsChange(items.filter((_, i) => i !== index));
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {items.map((d, i) => {
                const color = d.platform ? PLATFORM_COLORS[d.platform] ?? '#9e9e9e' : '#9e9e9e';
                const bg = d.platform ? PLATFORM_BG[d.platform] ?? '#9e9e9e18' : '#9e9e9e18';
                return (
                    <Box
                        key={i}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            p: 1.5,
                            borderRadius: 2,
                            bgcolor: bg,
                            border: `1px solid ${color}30`,
                        }}>
                        <Box
                            sx={{
                                width: 10,
                                height: 10,
                                borderRadius: '50%',
                                bgcolor: color,
                                flexShrink: 0,
                            }}
                        />

                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#fff', lineHeight: 1.3 }}>
                                {d.count > 1 ? `${d.count}× ` : ''}
                                {TYPE_LABELS[d.type] ?? d.type}
                            </Typography>
                            <Typography sx={{ fontSize: 11, color: '#9e9e9e', mt: 0.25 }}>
                                {d.platform ? d.platform.charAt(0).toUpperCase() + d.platform.slice(1) : ''}
                                {d.duration_range !== 'n/a' ? `${d.platform ? ' · ' : ''}${d.duration_range}` : ''}
                                {d.notes ? ` · ${d.notes}` : ''}
                            </Typography>
                        </Box>

                        <IconButton
                            size="small"
                            onClick={() => setEditIndex(i)}
                            sx={{ color: '#666', '&:hover': { color: '#fff' } }}>
                            <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                            size="small"
                            onClick={() => handleRemove(i)}
                            sx={{ color: '#666', '&:hover': { color: '#ef5350' } }}>
                            <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                    </Box>
                );
            })}

            <Button
                startIcon={<AddIcon />}
                onClick={() => setAddOpen(true)}
                sx={{
                    alignSelf: 'flex-start',
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: 13,
                    color: '#ffc965',
                    px: 0,
                    '&:hover': { bgcolor: 'transparent', opacity: 0.8 },
                }}>
                Add deliverable
            </Button>

            {/* Add modal */}
            <DeliverablePickerModal
                open={addOpen}
                onClose={() => setAddOpen(false)}
                onAdd={handleAdd}
                selectedPlatforms={selectedPlatforms}
                isDirectHire={isDirectHire}
                campaignType={campaignType}
            />

            {/* Edit modal */}
            <DeliverablePickerModal
                open={editIndex !== null}
                onClose={() => setEditIndex(null)}
                onAdd={handleEdit}
                existing={editIndex !== null ? items[editIndex] : undefined}
                selectedPlatforms={selectedPlatforms}
                isDirectHire={isDirectHire}
                campaignType={campaignType}
            />
        </Box>
    );
}
